<script lang="ts" module>
	// D8: an item's one photo (UC-9.R16), the design system's ImageInput as
	// D16 uses it for the logo, in the item's sheet. A picked photo is shrunk
	// at once (shrinkPhoto), to the centre square the server keeps, and waits
	// in the sheet, its preview shown, until the owner saves the item; so does
	// «امسح». The save sends it here, after the item itself is saved, because
	// a new item has no id to hang a photo on before that.
	//
	// The browser checks the type, the bytes and the pixel size first, as for
	// the logo, at the server's limits for an item photo: PNG, JPEG or WebP,
	// at most 20 MB, at least 64 by 64. The server crops it square, fits it to
	// 1024 by 1024 and stores it as WebP; it answers IMAGE_INVALID with the
	// same three reasons, so a file that slips past lands in the same words.

	import { api } from '../api/client';
	import type { components } from '../api/types';
	import type { MessageKey } from '../i18n/messages';

	type Item = components['schemas']['ItemBody'];

	export type PhotoProblem = 'type' | 'size' | 'small';

	/** The file types an item photo may be, as the server reads them. */
	export const PHOTO_TYPES = 'image/png,image/jpeg,image/webp';

	/** The largest file an item photo may be: the server's 20 MB. */
	export const PHOTO_MAX_BYTES = 20 * 1024 * 1024;

	export const photoWords: Record<PhotoProblem, MessageKey> = {
		type: 'menuPhotoType',
		size: 'menuPhotoSize',
		small: 'menuPhotoSmall',
	};

	export type PhotoAnswer =
		{ item: Item } | { problem: PhotoProblem } | { failure: string | undefined };

	/**
	 * Sends the photo the owner picked for an item, as the sheet shrank it,
	 * and answers the item as the owner's menu reads it, the problem the
	 * server found with the file, or a failure with the server's words when
	 * it had any.
	 */
	export async function sendPhoto(itemId: string, body: Blob): Promise<PhotoAnswer> {
		const { data, error } = await api.PUT('/api/owner/menu/items/{itemId}/photo', {
			params: { path: { itemId } },
			// The body is the picture itself; the generated type calls a
			// binary body a string.
			body: body as unknown as string,
			bodySerializer: (picture) => picture,
			headers: { 'Content-Type': 'application/octet-stream' },
		});
		if (error === undefined && data) return { item: data };
		const reason = String(error?.details?.reason ?? '');
		if (error?.code === 'IMAGE_INVALID' && Object.hasOwn(photoWords, reason)) {
			return { problem: reason as PhotoProblem };
		}
		return { failure: error?.message };
	}

	/** Removes an item's photo, and answers the item as the owner's menu reads it. */
	export async function clearPhoto(itemId: string): Promise<PhotoAnswer> {
		const { data, error } = await api.DELETE('/api/owner/menu/items/{itemId}/photo', {
			params: { path: { itemId } },
		});
		if (error === undefined && data) return { item: data };
		return { failure: error?.message };
	}

	/**
	 * The description a save sends: the one typed, when the owner changed it;
	 * none otherwise, so the stored description stays as it is (UC-9.R15,
	 * "An edit that leaves the description out keeps it"). An emptied one is
	 * sent empty, which clears it.
	 */
	export function descriptionEdit(typed: string, opened: string): { description?: string } {
		return typed === opened ? {} : { description: typed };
	}

	/**
	 * How the sheet ends a save, after the item itself is saved, by what the
	 * photo's call answered (null when none was made): it closes, or it stays
	 * open on the saved item with the photo's refusal under the photo, or the
	 * server's words above.
	 */
	export function photoOutcome(
		answer: PhotoAnswer | null,
	):
		| { close: true }
		| { close: false; problem: PhotoProblem }
		| { close: false; failure: string | undefined } {
		if (answer === null || 'item' in answer) return { close: true };
		if ('problem' in answer) return { close: false, problem: answer.problem };
		return { close: false, failure: answer.failure };
	}

	/**
	 * What the sheet's save does with the photo, after the item is saved:
	 * sends the one picked, removes the saved one when «امسح» was tapped, or
	 * nothing (null) when neither changed.
	 */
	export function savePhoto(
		itemId: string,
		picked: Blob | null,
		cleared: boolean,
		saved: string,
	): Promise<PhotoAnswer> | null {
		if (picked) return sendPhoto(itemId, picked);
		if (cleared && saved) return clearPhoto(itemId);
		return null;
	}
</script>

<script lang="ts">
	import ImageInput, { type Picked } from '../components/ImageInput.svelte';
	import { message, type Language } from '../i18n/messages';

	interface Props {
		language: Language;
		/** The photo shown: the one picked, or the item's own; none when empty. */
		src: string;
		problem: PhotoProblem | null;
		/** The item's sheet is saving. */
		busy: boolean;
		/** A picked photo is being shrunk. */
		preparing: boolean;
		onPick: (picked: Picked) => void;
		onRemove: () => void;
	}

	let { language, src, problem, busy, preparing, onPick, onRemove }: Props = $props();
</script>

<div class="photo">
	<span class="label">{message(language, 'menuPhoto')}</span>
	<ImageInput
		testId="item-photo"
		src={src || undefined}
		alt={message(language, 'menuPhotoAlt')}
		emptyLabel={message(language, 'menuPhotoEmpty')}
		addLabel={message(language, 'menuPhotoAdd')}
		replaceLabel={message(language, 'menuPhotoReplace')}
		removeLabel={message(language, 'menuPhotoRemove')}
		{onPick}
		{onRemove}
		accept={PHOTO_TYPES}
		maxBytes={PHOTO_MAX_BYTES}
		disabled={busy}
		state={preparing ? 'pending' : 'idle'}
		pendingLabel={message(language, 'menuPhotoPreparing')}
		error={problem ? message(language, photoWords[problem]) : undefined}
		note={message(language, 'menuPhotoNote')}
	/>
</div>

<style>
	.photo {
		display: flex;
		flex-direction: column;
		gap: var(--space-2);
	}

	.label {
		font-size: var(--text-2xs);
		color: var(--text-muted);
	}
</style>
